/**
 * Single source of truth for onboarding step/substep definitions.
 * All step configurations and validation utilities should derive from this file.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface SubstepConfig {
  key: string;
  name: string;
}

export interface StepConfigWithSubsteps {
  dataKey: string;
  name: string;
  stepNumber: number;
  substeps: SubstepConfig[];
}

export type ProductType = "returns" | "order-editing";

// ============================================================================
// RETURNS ONBOARDING STEP CONFIGURATION
// ============================================================================

export const RETURNS_STEP_CONFIG: StepConfigWithSubsteps[] = [
  {
    dataKey: "returns-onboarding",
    name: "Product Selection",
    stepNumber: 1,
    substeps: [
      { key: "industry-selection", name: "Industry Selection" },
      { key: "onboarding-summary", name: "Onboarding Summary" },
    ],
  },
  {
    dataKey: "policies",
    name: "Policies",
    stepNumber: 2,
    substeps: [
      { key: "overview", name: "Overview" },
      { key: "upload-policies", name: "Upload Policies" },
      { key: "return-window", name: "Return Window" },
      { key: "eligibility", name: "Eligibility" },
      { key: "policy-exclusions", name: "Exclusions" },
      { key: "return-reasons", name: "Return Reasons" },
    ],
  },
  {
    dataKey: "return-options",
    name: "Return Options",
    stepNumber: 3,
    substeps: [
      { key: "overview", name: "Overview" },
      { key: "return-methods", name: "Return Methods" },
      { key: "return-processing", name: "Return Processing" },
      { key: "exchange-products", name: "Exchange Products" },
      { key: "return-address", name: "Return Address" },
    ],
  },
  {
    dataKey: "returns-flow",
    name: "Return Flow",
    stepNumber: 4,
    substeps: [
      { key: "overview", name: "Overview" },
      { key: "how-return-flow-works", name: "How Return Flow Works" },
      { key: "flow-builder", name: "Flow Builder" },
      { key: "portal-testing", name: "Portal Testing" },
    ],
  },
  {
    dataKey: "coverage",
    name: "Coverage",
    stepNumber: 5,
    substeps: [
      { key: "overview", name: "Overview" },
      { key: "about-coverage", name: "About Coverage" },
      { key: "coverage-price", name: "Coverage Price" },
      { key: "coverage-exclusions", name: "Exclusions" },
      { key: "enable-app-embed", name: "Enable App Embed" },
      { key: "coverage-ui", name: "Coverage Widget" },
      { key: "confirmation", name: "Confirmation" },
      { key: "shipping-responsibility", name: "Shipping Responsibility" },
    ],
  },
  {
    dataKey: "review",
    name: "Review",
    stepNumber: 6,
    substeps: [
      { key: "overview", name: "Overview" },
      { key: "summary", name: "Summary" },
      { key: "your-policy", name: "Generate Policy" },
      { key: "communications", name: "Communications" },
      { key: "billing", name: "Billing" },
      { key: "integrations", name: "Integrations" },
      { key: "system-integrations-review", name: "Portal Link" },
    ],
  },
  { dataKey: "submit", name: "Go Live", stepNumber: 7, substeps: [] },
];

// ============================================================================
// ORDER EDITING ONBOARDING STEP CONFIGURATION
// ============================================================================

export const ORDER_EDITING_STEP_CONFIG: StepConfigWithSubsteps[] = [
  {
    dataKey: "order-editing",
    name: "Order Editing Setup",
    stepNumber: 1,
    substeps: [
      { key: "overview", name: "Welcome" },
      { key: "video-intro", name: "What is Order Editing" },
      { key: "enabled-actions", name: "What Can Customers Edit" },
      { key: "time-window", name: "Time Window" },
      { key: "widget-preview", name: "Widget Preview" },
      { key: "additional-platforms", name: "Additional Platforms" },
      { key: "video-app-embed", name: "Enable App Embed" },
      { key: "completion", name: "Completion" },
    ],
  },
];

// ============================================================================
// DERIVED VALIDATION DATA
// ============================================================================

/**
 * Convert step config to validation format: { stepId: [substepIds] }
 */
function configToValidationMap(
  config: StepConfigWithSubsteps[],
): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  for (const step of config) {
    result[step.dataKey] = step.substeps.map((s) => s.key);
  }
  return result;
}

/** Valid step/substep IDs for returns onboarding */
export const VALID_RETURNS_STEPS = configToValidationMap(RETURNS_STEP_CONFIG);

/** Valid step/substep IDs for order-editing onboarding */
export const VALID_ORDER_EDITING_STEPS = configToValidationMap(
  ORDER_EDITING_STEP_CONFIG,
);

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

/**
 * Get valid steps configuration for a given product
 */
export function getValidStepsForProduct(
  product: ProductType,
): Record<string, string[]> {
  switch (product) {
    case "returns":
      return VALID_RETURNS_STEPS;
    case "order-editing":
      return VALID_ORDER_EDITING_STEPS;
    default:
      return {};
  }
}

/**
 * Get step config for a given product
 */
export function getStepConfigForProduct(
  product: ProductType,
): StepConfigWithSubsteps[] {
  switch (product) {
    case "returns":
      return RETURNS_STEP_CONFIG;
    case "order-editing":
      return ORDER_EDITING_STEP_CONFIG;
    default:
      return [];
  }
}

export interface ValidationResult {
  valid: boolean;
  invalidSteps: string[];
  invalidSubsteps: { stepId: string; substepId: string }[];
}

/**
 * Validate that all step and substep IDs in the configuration are valid
 */
export function validateStepsConfig(
  stepsConfig: Record<string, string[]> | undefined,
  product: ProductType,
): ValidationResult {
  const result: ValidationResult = {
    valid: true,
    invalidSteps: [],
    invalidSubsteps: [],
  };

  if (!stepsConfig) {
    return result;
  }

  const validSteps = getValidStepsForProduct(product);

  for (const [stepId, substepIds] of Object.entries(stepsConfig)) {
    // Check if step ID is valid
    const validSubsteps = validSteps[stepId];
    if (!validSubsteps) {
      result.valid = false;
      result.invalidSteps.push(stepId);
      continue;
    }

    // Check if all substep IDs are valid
    for (const substepId of substepIds) {
      if (!validSubsteps.includes(substepId)) {
        result.valid = false;
        result.invalidSubsteps.push({ stepId, substepId });
      }
    }
  }

  return result;
}
